'use client';

import { loggingService } from './loggingService';
import { toast } from 'react-hot-toast';

// Error types
export enum ErrorType {
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NOT_FOUND = 'not_found',
  SERVER = 'server',
  NETWORK = 'network',
  UNKNOWN = 'unknown'
}

// Custom error class
export class AppError extends Error {
  public type: ErrorType;
  public statusCode?: number;
  public context?: Record<string, any>;

  constructor(
    message: string, 
    type: ErrorType = ErrorType.UNKNOWN, 
    statusCode?: number,
    context?: Record<string, any>
  ) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.statusCode = statusCode;
    this.context = context;
  }
}

// Function to parse API error responses
export function parseApiError(error: any): AppError {
  if (error instanceof AppError) {
    return error;
  }

  let message = 'An unknown error occurred';
  let type = ErrorType.UNKNOWN;
  let statusCode: number | undefined = undefined;
  let context: Record<string, any> | undefined = undefined;

  // Handle fetch Response errors
  if (error instanceof Response) {
    statusCode = error.status;
    message = `API Error: ${error.statusText}`;
    
    if (error.status === 401) {
      type = ErrorType.AUTHENTICATION;
      message = 'Authentication required';
    } else if (error.status === 403) {
      type = ErrorType.AUTHORIZATION;
      message = 'You do not have permission to perform this action';
    } else if (error.status === 404) {
      type = ErrorType.NOT_FOUND;
      message = 'Resource not found';
    } else if (error.status >= 500) {
      type = ErrorType.SERVER;
      message = 'Server error occurred';
    }
  } 
  // Handle network errors
  else if (error instanceof TypeError && error.message.includes('fetch')) {
    type = ErrorType.NETWORK;
    message = 'Network error occurred. Please check your connection';
  } 
  // Handle Error objects
  else if (error instanceof Error) {
    message = error.message;
    context = { stack: error.stack };
  } 
  // Handle error objects with message property
  else if (error && typeof error === 'object' && 'message' in error) {
    message = String(error.message);
    
    // Check for api response object structure
    if ('statusCode' in error) {
      statusCode = Number(error.statusCode);
    }
    
    if ('type' in error && Object.values(ErrorType).includes(error.type as ErrorType)) {
      type = error.type as ErrorType;
    }
    
    // Extract any additional context
    if (error) {
      context = { ...error };
      if (context) {
        delete context.message;
        delete context.type;
        delete context.statusCode;
      }
    }
  }
  // Handle string errors
  else if (typeof error === 'string') {
    message = error;
  }

  return new AppError(message, type, statusCode, context);
}

// Error handling service
class ErrorHandlingService {
  // Handle errors in try/catch blocks
  handleError(error: unknown, context?: Record<string, any>): AppError {
    const appError = parseApiError(error);
    
    // Log the error based on its severity
    if (appError.type === ErrorType.SERVER || appError.type === ErrorType.UNKNOWN) {
      loggingService.error(appError.message, error, {
        ...appError.context,
        ...context,
        errorType: appError.type,
        statusCode: appError.statusCode
      });
    } else {
      loggingService.warn(appError.message, {
        ...appError.context,
        ...context,
        errorType: appError.type,
        statusCode: appError.statusCode
      });
    }
    
    return appError;
  }

  // Display error to user with toast notification
  showErrorToast(error: unknown, defaultMessage: string = 'An error occurred'): void {
    const appError = error instanceof AppError ? error : parseApiError(error);
    const message = appError.message || defaultMessage;
    
    toast.error(message, {
      duration: 5000,
      position: 'top-center'
    });
  }

  // Display a success message
  showSuccessToast(message: string): void {
    toast.success(message, {
      duration: 3000,
      position: 'top-center'
    });
  }

  // Get a user-friendly error message based on error type
  getUserFriendlyMessage(error: unknown): string {
    const appError = error instanceof AppError ? error : parseApiError(error);
    
    switch (appError.type) {
      case ErrorType.VALIDATION:
        return appError.message || 'Please check your input and try again';
      case ErrorType.AUTHENTICATION:
        return 'You need to login to continue';
      case ErrorType.AUTHORIZATION:
        return 'You do not have permission to perform this action';
      case ErrorType.NOT_FOUND:
        return 'The requested resource could not be found';
      case ErrorType.NETWORK:
        return 'Network connection issue. Please check your internet connection';
      case ErrorType.SERVER:
        return 'The server encountered an error. Please try again later';
      default:
        return appError.message || 'An unexpected error occurred';
    }
  }

  // Track API performance
  trackApiPerformance(apiName: string, startTime: number, success: boolean): void {
    const duration = performance.now() - startTime;
    loggingService.info(`API call to ${apiName} completed`, {
      apiName,
      durationMs: duration,
      success
    });
  }
}

// Export a singleton instance
export const errorHandlingService = new ErrorHandlingService(); 